/**
 * Pure geometry for the stack-mode deck (Figma node 1588-175444).
 *
 * The deck inverts the Clarify stack's semantics: the edges peeking ABOVE the
 * focus card are the NEXT tasks (a card deck), not the scroll history.
 * Scrolling forward fades the focus card out downwards and pulls the deck one
 * position forward; scrolling back brings the previous card back in from
 * below. All positions derive continuously from the scroll progress
 * `s = scrollTop / CARD_PITCH`; a card's signed deck position is `p = idx - s`:
 *
 *   p < 0        → outgoing (scrolled past): slides down + fades out
 *   p = 0        → focus card (interactive)
 *   0 < p ≤ 3    → edge strips behind/above the focus card
 *   p > 3        → hidden behind the deepest edge (fades out)
 */

// One "card deal" = this many px of scroll (matches the Clarify feel).
export const CARD_PITCH = 88
// Visible strip height per edge (13px Figma card minus its 2px bottom border).
export const EDGE_PITCH = 11
// Max edges behind the focus card ("Einsortieren" shows 2, the deck shows 3).
export const EDGE_COUNT = 3
// Y of the focus card's top inside the deck canvas (edges stack above it).
export const DECK_TOP = EDGE_COUNT * EDGE_PITCH
// Clipped strip height for edge cards (rounded top only, Figma: 13px).
export const EDGE_STRIP_HEIGHT = 13
// Horizontal inset per side at depth 0..3 (Figma: px-2/px-5/px-8 → 8/20/32).
export const EDGE_INSETS = [0, 8, 20, 32]
// Cover tint strength per depth 1..3 (Figma: 18% / 8% / 4%).
export const EDGE_TINTS = [0.18, 0.08, 0.04]
// Zone (in card units) over which an edge's cover fades out while it morphs
// into the focus slot — content becomes readable only near the front.
export const COVER_FADE_ZONE = 0.35
// Outgoing card: distance travelled downwards + fade length in card units.
export const OUTGOING_DISTANCE = 56
export const OUTGOING_FADE = 0.6
// Fade length behind the deepest edge.
export const BACK_FADE = 0.5
// Clip ceiling while un-clipping toward the focus slot (≥ any real card
// height — the focus card renders as a fully expanded TaskEntry).
export const CARD_MAX_HEIGHT = 240
// The focus card counts as interactive while |p| is below this threshold.
export const INTERACTIVE_EPSILON = 0.5

export interface DeckSlot {
  /** Y offset of the card's top inside the deck canvas. */
  y: number
  /** Horizontal inset per side. */
  insetX: number
  /** Whole-slot opacity (outgoing + behind-deck fades). */
  opacity: number
  /** Opacity of the blank cover hiding the card content (0 on the focus card). */
  coverOpacity: number
  /** Tint strength of the cover (depth-dependent, see EDGE_TINTS). */
  coverTint: number
  /** Clip height in px, or null for "unclipped" (focus/outgoing cards). */
  clipHeight: number | null
  zIndex: number
  /** Only the settled focus card receives pointer events. */
  interactive: boolean
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v))
}

/** Piecewise-linear inset per side for a continuous depth p ≥ 0. */
export function edgeInset(p: number): number {
  const d = Math.min(Math.max(p, 0), EDGE_COUNT)
  const lo = Math.floor(d)
  const hi = Math.min(lo + 1, EDGE_COUNT)
  return lerp(EDGE_INSETS[lo], EDGE_INSETS[hi], d - lo)
}

/** Depth-dependent cover tint (constant 18% until depth 1, then easing back). */
export function edgeTint(p: number): number {
  if (p <= 1) return EDGE_TINTS[0]
  if (p <= 2) return lerp(EDGE_TINTS[0], EDGE_TINTS[1], p - 1)
  if (p <= 3) return lerp(EDGE_TINTS[1], EDGE_TINTS[2], p - 2)
  return EDGE_TINTS[2]
}

/**
 * Visual state for card `idx` at scroll progress `s` (both continuous).
 * Later-indexed (deeper) cards always paint BELOW earlier ones — the deck's
 * focus card is on top, outgoing cards (in front, fading) above it.
 */
export function computeDeckSlot(idx: number, s: number): DeckSlot {
  const p = idx - s
  const zIndex = 10000 - idx

  if (p < 0) {
    // Outgoing: the focus card slides down and dissolves.
    const t = clamp01(-p / OUTGOING_FADE)
    return {
      y: DECK_TOP + t * OUTGOING_DISTANCE,
      insetX: 0,
      opacity: 1 - t,
      coverOpacity: 0,
      coverTint: 0,
      clipHeight: null,
      zIndex,
      interactive: false,
    }
  }

  // Focus → edges → hidden.
  const clippedT = clamp01(p) // 0 = focus slot, 1 = fully in edge-strip form
  const behindT = p > EDGE_COUNT ? clamp01((p - EDGE_COUNT) / BACK_FADE) : 0
  return {
    y: DECK_TOP - Math.min(p, EDGE_COUNT) * EDGE_PITCH,
    insetX: edgeInset(p),
    opacity: 1 - behindT,
    coverOpacity: clamp01(p / COVER_FADE_ZONE),
    coverTint: edgeTint(p),
    clipHeight: p <= 0 ? null : lerp(CARD_MAX_HEIGHT, EDGE_STRIP_HEIGHT, clippedT),
    zIndex,
    interactive: p < INTERACTIVE_EPSILON,
  }
}
